import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Save } from 'lucide-react';
import { Botao, Cartao, Indicador, Segmentado } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { anunciarOcultos, Sensivel, textoSensivel, useDiscreto } from '../../componentes/sensivel';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, patch } from '../../nucleo/api';
import { compacto, data, dataHora, dinheiro, mesAno, numero, percentual } from '../../nucleo/formato';
import { GRANULARIDADES_USO_IA, type GranularidadeUsoIa } from '@malgoos/shared';

/**
 * Painel de uso de IA (01/11, ficha `2026-10-30_malgo_FUN_painel-uso-de-ia`): Admin > Painel admin > Uso de IA. Só o
 * administrador da plataforma (aba `plataforma`; o servidor confere de novo). Tokens do Claude Code medidos no Mac
 * (`scripts/uso-ia/medir.mjs`) por dia, semana, mês e ano, com o custo equivalente em preço de API (US$, tabela oficial da
 * Anthropic) e em R$ pela cotação configurada; % de participação no período e variação contra o período anterior.
 */

export interface TotaisUsoIa {
  mensagens: number;
  entrada: number;
  cache_5m: number;
  cache_1h: number;
  cache_leitura: number;
  saida: number;
  total: number;
  custo_usd: number | null;
  tokens_sem_preco: number;
}
export interface FatiaUsoIa extends TotaisUsoIa {
  chave: string;
  participacao_custo: number | null;
  participacao_tokens: number | null;
  variacao_custo: number | null;
  variacao_tokens: number | null;
}
export interface DadosUsoIa {
  granularidade: GranularidadeUsoIa;
  hoje: string;
  periodo: string;
  periodo_anterior: string;
  serie: (TotaisUsoIa & { periodo: string })[];
  atual: TotaisUsoIa;
  anterior: TotaisUsoIa;
  variacao_custo: number | null;
  variacao_tokens: number | null;
  por_modelo: FatiaUsoIa[];
  por_projeto: FatiaUsoIa[];
  por_tipo: FatiaUsoIa[];
  projetos: string[];
  ultimo_dia: string | null;
  ultimo_envio: string | null;
  cotacao_brl: number | null;
  cotacao_em: string | null;
  precos: { fonte: string; conferidos_em: string };
}

const CHAVE = '/nucleo/uso-ia';

/** Dia (aaaa-mm-dd) deslocado em n períodos, a partir do início do período (as contas de calendário em UTC). */
export function periodoVizinho(g: GranularidadeUsoIa, inicio: string, n: number): string {
  const d = new Date(`${inicio}T00:00:00Z`);
  if (g === 'dia') d.setUTCDate(d.getUTCDate() + n);
  else if (g === 'semana') d.setUTCDate(d.getUTCDate() + 7 * n);
  else if (g === 'mes') d.setUTCMonth(d.getUTCMonth() + n, 1);
  else d.setUTCFullYear(d.getUTCFullYear() + n, 0, 1);
  return d.toISOString().slice(0, 10);
}

/** Sem valor (sem preço, sem cotação ou sem base de comparação). */
const VAZIO = '-';
const usd = (v: number | null) => (v === null ? '' : dinheiro(Math.round(v * 100), 'USD'));
const brl = (v: number | null, cotacao: number | null) => (v === null || cotacao === null ? '' : dinheiro(Math.round(v * cotacao * 100), 'BRL'));
const pct = (v: number | null) => (v === null ? VAZIO : `${percentual(v)}%`);
const sinal = (v: number | null) => (v === null ? VAZIO : `${v > 0 ? '+' : ''}${percentual(v)}%`);

function useRotuloPeriodo() {
  const { t } = useTranslation('admin');
  return (g: GranularidadeUsoIa, p: string) => (g === 'dia' ? data(p) : g === 'semana' ? t('usoIa.semanaDe', { data: data(p) }) : g === 'mes' ? mesAno(p.slice(0, 7)) : p.slice(0, 4));
}

/** Barras do custo (US$) por período; sem preço, as barras são dos tokens. SVG nos tokens, tabela só para leitor de tela. */
function GraficoUsoIa({ d }: { d: DadosUsoIa }) {
  const { t } = useTranslation('admin');
  // Rodada ruso-1 (VIS-B2): no modo discreto o título da barra fica só com o período (o valor oculto viraria pontos soletrados).
  const { ativo: discreto } = useDiscreto();
  const rotulo = useRotuloPeriodo();
  const porCusto = d.serie.some((p) => (p.custo_usd ?? 0) > 0);
  const valor = (p: TotaisUsoIa) => (porCusto ? (p.custo_usd ?? 0) : p.total);
  const fmt = (v: number) => (porCusto ? usd(v) : compacto(v));
  const n = Math.max(1, d.serie.length);
  const maximo = Math.max(1e-9, ...d.serie.map(valor));
  return (
    <figure className="min-w-0" data-uso-ia-grafico={porCusto ? 'custo' : 'tokens'}>
      <div className="text-xs text-tinta-3">
        <span className="numero">{anunciarOcultos(fmt(maximo))}</span>
      </div>
      <svg viewBox={`0 0 ${n * 10} 100`} preserveAspectRatio="none" className="mt-1 h-40 w-full" role="img" aria-label={t('usoIa.graficoAria', { de: rotulo(d.granularidade, d.serie[0]?.periodo ?? d.periodo), ate: rotulo(d.granularidade, d.periodo) })}>
        <line x1={0} x2={n * 10} y1={100} y2={100} className="stroke-borda" strokeWidth={1} vectorEffect="non-scaling-stroke" />
        {d.serie.map((p, k) => {
          const h = (valor(p) / maximo) * 100;
          return (
            <rect key={p.periodo} x={k * 10 + 1.5} width={7} y={100 - h} height={h} className={p.periodo === d.periodo ? 'fill-acento-forte' : 'fill-apoio'}>
              <title>{discreto ? rotulo(d.granularidade, p.periodo) : `${rotulo(d.granularidade, p.periodo)}: ${fmt(valor(p))}`}</title>
            </rect>
          );
        })}
      </svg>
      <div className="mt-1 flex justify-between gap-2 text-xs text-tinta-3">
        <span className="numero">{rotulo(d.granularidade, d.serie[0]?.periodo ?? d.periodo)}</span>
        <span className="numero">{rotulo(d.granularidade, d.periodo)}</span>
      </div>
      <figcaption className="mt-2 text-xs text-tinta-2">{porCusto ? t('usoIa.graficoCusto') : t('usoIa.graficoTokens')}</figcaption>
    </figure>
  );
}

/**
 * `nome`: a chave é nome de pasta do Mac (projeto), que pode trazer nome de cliente; segue a opção de nomes do modo discreto
 * (iniciais), na célula e no `title` (rodada ruso-1, VIS-B3).
 */
function TabelaFatias({ titulo, fatias, rotuloChave, cotacao, id, nome }: { titulo: string; fatias: FatiaUsoIa[]; rotuloChave: (c: string) => string; cotacao: number | null; id: string; nome?: boolean }) {
  const { t } = useTranslation('admin');
  useDiscreto();
  return (
    <Cartao titulo={titulo} semPadding>
      {fatias.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-tinta-3">{t('usoIa.semUsoNoPeriodo')}</p>
      ) : (
        <div className="overflow-x-auto" data-uso-ia-tabela={id}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-borda text-left text-tinta-3">
                <th scope="col" className="px-4 py-2 font-medium">{titulo}</th>
                <th scope="col" className="px-4 py-2 text-right font-medium">{t('usoIa.col.mensagens')}</th>
                <th scope="col" className="px-4 py-2 text-right font-medium">{t('usoIa.col.entrada')}</th>
                <th scope="col" className="px-4 py-2 text-right font-medium">{t('usoIa.col.cacheEscrito')}</th>
                <th scope="col" className="px-4 py-2 text-right font-medium">{t('usoIa.col.cacheLido')}</th>
                <th scope="col" className="px-4 py-2 text-right font-medium">{t('usoIa.col.saida')}</th>
                <th scope="col" className="px-4 py-2 text-right font-medium">{t('usoIa.col.total')}</th>
                <th scope="col" className="px-4 py-2 text-right font-medium">{t('usoIa.col.usd')}</th>
                <th scope="col" className="px-4 py-2 text-right font-medium">{t('usoIa.col.brl')}</th>
                <th scope="col" className="px-4 py-2 text-right font-medium">{t('usoIa.col.participacao')}</th>
                <th scope="col" className="px-4 py-2 text-right font-medium">{t('usoIa.col.variacao')}</th>
              </tr>
            </thead>
            <tbody>
              {fatias.map((f) => (
                <tr key={f.chave} className="border-b border-borda last:border-0" data-uso-ia-linha={f.chave}>
                  <th scope="row" className="max-w-56 truncate px-4 py-2 text-left font-medium text-tinta-1" title={nome ? textoSensivel(rotuloChave(f.chave), 'nome') : rotuloChave(f.chave)}>
                    {nome ? <Sensivel tipo="nome">{rotuloChave(f.chave)}</Sensivel> : rotuloChave(f.chave)}
                  </th>
                  <td className="numero px-4 py-2 text-right">{numero(f.mensagens)}</td>
                  <td className="numero px-4 py-2 text-right" title={numero(f.entrada)}>{compacto(f.entrada)}</td>
                  <td className="numero px-4 py-2 text-right" title={numero(f.cache_5m + f.cache_1h)}>{compacto(f.cache_5m + f.cache_1h)}</td>
                  <td className="numero px-4 py-2 text-right" title={numero(f.cache_leitura)}>{compacto(f.cache_leitura)}</td>
                  <td className="numero px-4 py-2 text-right" title={numero(f.saida)}>{compacto(f.saida)}</td>
                  <td className="numero px-4 py-2 text-right" title={numero(f.total)}>{compacto(f.total)}</td>
                  <td className="numero px-4 py-2 text-right">{f.custo_usd === null ? <span title={t('usoIa.semPreco')}>{VAZIO}</span> : anunciarOcultos(usd(f.custo_usd))}</td>
                  <td className="numero px-4 py-2 text-right">{f.custo_usd === null || cotacao === null ? VAZIO : anunciarOcultos(brl(f.custo_usd, cotacao))}</td>
                  <td className="numero px-4 py-2 text-right" title={t('usoIa.participacaoTokens', { valor: pct(f.participacao_tokens) })}>{pct(f.participacao_custo ?? f.participacao_tokens)}</td>
                  <td className="numero px-4 py-2 text-right">{sinal(f.variacao_custo ?? f.variacao_tokens)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Cartao>
  );
}

function TabelaSerie({ d }: { d: DadosUsoIa }) {
  const { t } = useTranslation('admin');
  const rotulo = useRotuloPeriodo();
  const linhas = [...d.serie].reverse();
  return (
    <Cartao titulo={t('usoIa.serie')} semPadding>
      <div className="overflow-x-auto" data-uso-ia-tabela="serie">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-borda text-left text-tinta-3">
              <th scope="col" className="px-4 py-2 font-medium">{t('usoIa.col.periodo')}</th>
              <th scope="col" className="px-4 py-2 text-right font-medium">{t('usoIa.col.mensagens')}</th>
              <th scope="col" className="px-4 py-2 text-right font-medium">{t('usoIa.col.total')}</th>
              <th scope="col" className="px-4 py-2 text-right font-medium">{t('usoIa.col.usd')}</th>
              <th scope="col" className="px-4 py-2 text-right font-medium">{t('usoIa.col.brl')}</th>
              <th scope="col" className="px-4 py-2 text-right font-medium">{t('usoIa.col.variacao')}</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((p, i) => {
              const ant = linhas[i + 1];
              const v = ant ? ((p.custo_usd ?? 0) > 0 || (ant.custo_usd ?? 0) > 0 ? varia(p.custo_usd, ant.custo_usd) : varia(p.total, ant.total)) : null;
              return (
                <tr key={p.periodo} className={`border-b border-borda last:border-0 ${p.periodo === d.periodo ? 'bg-acento-suave' : ''}`} data-uso-ia-periodo={p.periodo}>
                  <th scope="row" className="px-4 py-2 text-left font-medium text-tinta-1">{rotulo(d.granularidade, p.periodo)}</th>
                  <td className="numero px-4 py-2 text-right">{numero(p.mensagens)}</td>
                  <td className="numero px-4 py-2 text-right" title={numero(p.total)}>{compacto(p.total)}</td>
                  <td className="numero px-4 py-2 text-right">{p.custo_usd === null ? VAZIO : anunciarOcultos(usd(p.custo_usd))}</td>
                  <td className="numero px-4 py-2 text-right">{p.custo_usd === null || d.cotacao_brl === null ? VAZIO : anunciarOcultos(brl(p.custo_usd, d.cotacao_brl))}</td>
                  <td className="numero px-4 py-2 text-right">{sinal(v)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Cartao>
  );
}

/** Variação percentual; `null` sem base (anterior zero ou sem valor). A mesma regra da API. */
export function varia(atual: number | null, anterior: number | null): number | null {
  if (atual === null || anterior === null || anterior === 0) return null;
  return ((atual - anterior) / anterior) * 100;
}

/** Cotação do dólar (R$ por US$). Aceita vírgula ou ponto; o servidor arredonda a 4 casas e audita a troca. */
function CartaoCotacao({ atual, em }: { atual: number | null; em: string | null }) {
  const { t } = useTranslation('admin');
  const qc = useQueryClient();
  const toast = useToast();
  const texto = (v: number | null) => (v === null ? '' : numero(v, 4).replace(/0{1,2}$/, ''));
  const [valor, setValor] = useState(() => texto(atual));
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => setValor(texto(atual)), [atual]);
  const salvar = useMutation({
    // Rodada ruso-1 (ROB-M1): o texto vai como foi digitado; o servidor entende vírgula ou ponto decimal (5,43 e 5.43).
    mutationFn: (v: string) => patch(`${CHAVE}/cotacao`, { cotacao_brl: v }),
    onSuccess: async () => {
      setErro(null);
      await qc.invalidateQueries({ queryKey: [CHAVE] });
      toast.sucesso(t('usoIa.cotacao.salva'));
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  const id = 'uso-ia-cotacao';
  return (
    <Cartao titulo={t('usoIa.cotacao.titulo')}>
      <form
        className="flex min-w-0 flex-col gap-3"
        data-uso-ia-cotacao
        onSubmit={(e) => {
          e.preventDefault();
          const v = valor.trim();
          if (!v) return;
          // Rodada ruso-2 (VIS-B4): número inválido tem mensagem própria; a regra fina (até 1.000, 4 casas) fica no servidor.
          if (!/^\d+([.,]\d+)?$/.test(v) || !(Number(v.replace(',', '.')) > 0)) return setErro(t('usoIa.cotacao.invalida'));
          salvar.mutate(v);
        }}
      >
        {/* Rodada ruso-1 (VIS-B1): o erro fica no Rotulo (ícone e id ligados ao campo); o aviso falado continua no alerta. */}
        <Rotulo id={id} rotulo={t('usoIa.cotacao.rotulo')} erro={erro ?? undefined} ajuda={em ? t('usoIa.cotacao.atualizada', { quando: dataHora(em) }) : t('usoIa.cotacao.semCotacao')}>
          <Entrada id={id} inputMode="decimal" autoComplete="off" maxLength={20} value={valor} aria-describedby={erro ? `${id}-erro` : `${id}-ajuda`} aria-invalid={!!erro} onChange={(e) => setValor(e.target.value)} />
        </Rotulo>
        {erro && (
          <p role="alert" className="sr-only" data-uso-ia-cotacao-erro>
            {erro}
          </p>
        )}
        <div className="flex justify-end">
          <Botao type="submit" variante="primario" icone={<Save className="size-4" aria-hidden />} carregando={salvar.isPending} disabled={!valor.trim() || valor.trim() === texto(atual)}>
            {t('usoIa.cotacao.salvar')}
          </Botao>
        </div>
      </form>
    </Cartao>
  );
}

export function UsoIa() {
  const { t } = useTranslation('admin');
  const rotulo = useRotuloPeriodo();
  const [g, setG] = useState<GranularidadeUsoIa>('dia');
  const [periodo, setPeriodo] = useState<string | null>(null);
  const [projeto, setProjeto] = useState('');
  const [tipo, setTipo] = useState<'' | 'principal' | 'agente'>('');
  const params = new URLSearchParams({ granularidade: g });
  if (periodo) params.set('periodo', periodo);
  if (projeto) params.set('projeto', projeto);
  if (tipo) params.set('tipo', tipo);
  const consulta = useQuery({ queryKey: [CHAVE, g, periodo, projeto, tipo], queryFn: () => get<DadosUsoIa>(`${CHAVE}?${params}`), placeholderData: keepPreviousData });
  const rotuloTipo = (c: string) => (c === 'principal' ? t('usoIa.tipo.principal') : c === 'agente' ? t('usoIa.tipo.agente') : c);
  return (
    <div className="flex min-w-0 flex-col gap-4" data-uso-ia>
      <p className="text-sm text-tinta-2">{t('usoIa.ajuda')}</p>
      <Estado consulta={consulta}>
        {(d) => {
          const semNada = d.ultimo_dia === null;
          const atualEhHoje = d.periodo === d.serie.at(-1)?.periodo && periodoVizinho(g, d.periodo, 1) > d.hoje;
          return (
            <>
              <div className="flex min-w-0 flex-wrap items-end gap-3">
                <Segmentado
                  rotulo={t('usoIa.granularidade')}
                  valor={g}
                  opcoes={GRANULARIDADES_USO_IA.map((v) => ({ valor: v, rotulo: t(`usoIa.por.${v}`) }))}
                  aoMudar={(v) => {
                    setG(v);
                    setPeriodo(null);
                  }}
                  data-uso-ia-granularidade=""
                />
                <div className="flex items-center gap-1" role="group" aria-label={t('usoIa.navegar')}>
                  <Botao type="button" variante="fantasma" aria-label={t('usoIa.anterior')} icone={<ChevronLeft className="size-4" aria-hidden />} onClick={() => setPeriodo(periodoVizinho(g, d.periodo, -1))} data-uso-ia-anterior />
                  <span className="min-w-28 text-center text-sm font-medium text-tinta-1" aria-live="polite" data-uso-ia-periodo-atual>
                    {rotulo(g, d.periodo)}
                  </span>
                  <Botao type="button" variante="fantasma" aria-label={t('usoIa.proximo')} icone={<ChevronRight className="size-4" aria-hidden />} disabled={atualEhHoje} onClick={() => setPeriodo(periodoVizinho(g, d.periodo, 1))} data-uso-ia-proximo />
                </div>
                <div className="w-full min-w-0 sm:w-56">
                  <Rotulo id="uso-ia-projeto" rotulo={t('usoIa.filtroProjeto')}>
                    <Selecao id="uso-ia-projeto" value={projeto} vazio={t('usoIa.todos')} opcoes={d.projetos.map((p) => ({ valor: p, rotulo: textoSensivel(p, 'nome') }))} onChange={(e) => setProjeto(e.target.value)} />
                  </Rotulo>
                </div>
                <div className="w-full min-w-0 sm:w-48">
                  <Rotulo id="uso-ia-tipo" rotulo={t('usoIa.filtroTipo')}>
                    <Selecao
                      id="uso-ia-tipo"
                      value={tipo}
                      vazio={t('usoIa.todos')}
                      opcoes={[
                        { valor: 'principal', rotulo: t('usoIa.tipo.principal') },
                        { valor: 'agente', rotulo: t('usoIa.tipo.agente') },
                      ]}
                      onChange={(e) => setTipo(e.target.value as typeof tipo)}
                    />
                  </Rotulo>
                </div>
              </div>
              {semNada ? (
                <Vazio titulo={t('usoIa.vazio')} texto={t('usoIa.vazioTexto')} />
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <Indicador rotulo={t('usoIa.ind.tokens')} valor={compacto(d.atual.total)} detalhe={t('usoIa.ind.mensagens', { n: numero(d.atual.mensagens) })} data-uso-ia-ind="tokens" />
                    <Indicador rotulo={t('usoIa.ind.usd')} valor={d.atual.custo_usd === null ? VAZIO : usd(d.atual.custo_usd)} detalhe={d.atual.tokens_sem_preco > 0 ? t('usoIa.ind.semPreco', { n: compacto(d.atual.tokens_sem_preco) }) : undefined} data-uso-ia-ind="usd" />
                    <Indicador rotulo={t('usoIa.ind.brl')} valor={d.atual.custo_usd === null || d.cotacao_brl === null ? VAZIO : brl(d.atual.custo_usd, d.cotacao_brl)} detalhe={d.cotacao_brl === null ? t('usoIa.cotacao.semCotacao') : t('usoIa.ind.cotacao', { valor: percentual(d.cotacao_brl, 4) })} data-uso-ia-ind="brl" />
                    <Indicador
                      rotulo={t('usoIa.ind.variacao')}
                      valor={sinal(d.variacao_custo ?? d.variacao_tokens)}
                      detalhe={t('usoIa.ind.contra', { periodo: rotulo(g, d.periodo_anterior) })}
                      cor={(d.variacao_custo ?? d.variacao_tokens ?? 0) > 0 ? 'laranja' : 'tinta-1'}
                      data-uso-ia-ind="variacao"
                    />
                  </div>
                  <Cartao titulo={t('usoIa.grafico')}>
                    <GraficoUsoIa d={d} />
                  </Cartao>
                  <TabelaFatias id="modelo" titulo={t('usoIa.porModelo')} fatias={d.por_modelo} rotuloChave={(c) => c} cotacao={d.cotacao_brl} />
                  <TabelaFatias id="projeto" titulo={t('usoIa.porProjeto')} fatias={d.por_projeto} rotuloChave={(c) => c} cotacao={d.cotacao_brl} nome />
                  <TabelaFatias id="tipo" titulo={t('usoIa.porTipo')} fatias={d.por_tipo} rotuloChave={rotuloTipo} cotacao={d.cotacao_brl} />
                  <TabelaSerie d={d} />
                </>
              )}
              <CartaoCotacao atual={d.cotacao_brl} em={d.cotacao_em} />
              <p className="text-xs text-tinta-3" data-uso-ia-rodape>
                {t('usoIa.rodape', { data: data(d.precos.conferidos_em) })}{' '}
                <a href={d.precos.fonte} target="_blank" rel="noreferrer noopener" className="underline">
                  {t('usoIa.fonte')}
                </a>
                {d.ultimo_envio ? ` · ${t('usoIa.ultimoEnvio', { quando: dataHora(d.ultimo_envio), dia: data(d.ultimo_dia) })}` : ''}
              </p>
            </>
          );
        }}
      </Estado>
    </div>
  );
}
